import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { sendContact } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import RichText from "./RichText";
import { toast } from "sonner";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Compila nome, email e messaggio.");
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast.success("Messaggio inviato. Ti risponderemo presto.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="tr-dark px-6 sm:px-12 py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-5">
          {settings.contact_eyebrow && settings.contact_eyebrow.trim() && (
            <p
              className="label-eyebrow"
              style={{ color: "rgba(248,247,244,0.6)" }}
              data-testid="contact-eyebrow"
            >
              {settings.contact_eyebrow}
            </p>
          )}
          {(settings.contact_title_a || settings.contact_title_b) && (
            <h2
              data-testid="contact-title"
              className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-6 leading-[0.95]"
            >
              {settings.contact_title_a}
              {settings.contact_title_a && settings.contact_title_b && <br />}
              {settings.contact_title_b && (
                <span className="italic" style={{ color: "rgba(248,247,244,0.6)" }}>
                  {settings.contact_title_b}
                </span>
              )}
            </h2>
          )}
          {settings.contact_text && settings.contact_text.trim() && (
            <RichText
              value={settings.contact_text}
              className="font-body mt-10 max-w-md text-[rgba(248,247,244,0.7)] leading-relaxed"
            />
          )}

          <div className="mt-12 space-y-6">
            {(settings.contact_items || []).filter((it) => it.enabled !== false).map((it) => {
              const v = (it.value || "").trim();
              const href = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
                ? `mailto:${v}`
                : /^\+?\d[\d\s().-]{5,}$/.test(v)
                ? `tel:${v.replace(/\s+/g, "")}`
                : /^(https?:)?\/\//i.test(v)
                ? v
                : null;
              return (
                <div key={it.id} data-testid={`contact-item-${it.id}`}>
                  {it.label && (
                    <p className="label-eyebrow" style={{ color: "rgba(248,247,244,0.5)" }}>
                      {it.label}
                    </p>
                  )}
                  {href ? (
                    <a
                      href={href}
                      className="font-display text-2xl mt-2 block hover:text-[color:var(--tr-accent)] transition-colors"
                    >
                      {it.value}
                    </a>
                  ) : (
                    <p className="font-display text-2xl mt-2 whitespace-pre-wrap">{it.value}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={handleSubmit}
          className="col-span-12 md:col-span-6 md:col-start-7 mt-10 md:mt-2"
        >
          <div
            className="space-y-2"
            style={{ "--tr-border": "rgba(248,247,244,0.25)" }}
          >
            <FormRow>
              <input
                data-testid="contact-input-name"
                type="text"
                placeholder="Nome e cognome"
                value={form.name}
                onChange={handleChange("name")}
                className="tr-field"
                style={{
                  color: "var(--tr-bg)",
                  borderBottomColor: "rgba(248,247,244,0.25)",
                }}
              />
            </FormRow>
            <FormRow>
              <input
                data-testid="contact-input-email"
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={handleChange("email")}
                className="tr-field"
                style={{
                  color: "var(--tr-bg)",
                  borderBottomColor: "rgba(248,247,244,0.25)",
                }}
              />
            </FormRow>
            <FormRow>
              <input
                data-testid="contact-input-phone"
                type="tel"
                placeholder="Telefono (opzionale)"
                value={form.phone}
                onChange={handleChange("phone")}
                className="tr-field"
                style={{
                  color: "var(--tr-bg)",
                  borderBottomColor: "rgba(248,247,244,0.25)",
                }}
              />
            </FormRow>
            <FormRow>
              <textarea
                data-testid="contact-input-message"
                placeholder="Raccontaci il progetto"
                rows={5}
                value={form.message}
                onChange={handleChange("message")}
                className="tr-field resize-none"
                style={{
                  color: "var(--tr-bg)",
                  borderBottomColor: "rgba(248,247,244,0.25)",
                }}
              />
            </FormRow>
          </div>

          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="mt-10 inline-flex items-center gap-3 font-sub text-[0.78rem] uppercase tracking-[0.24em] border-b border-[color:var(--tr-bg)] pb-2 hover:text-[color:var(--tr-accent)] hover:border-[color:var(--tr-accent)] transition-colors disabled:opacity-50"
          >
            {loading ? "Invio in corso…" : "Invia messaggio"}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.25} />
          </button>
        </form>
      </div>
    </section>
  );
}

function FormRow({ children }) {
  return <div className="py-1">{children}</div>;
}
